import {
  formatTimestamp,
  dateToISOString,
  getCurrentDate,
  parseTimestamp,
  formatRelativeDate,
  DATE_FORMAT_TOKENS,
} from '@/shared/utils/datetime-utils';
import {
  V1AutomationStage,
  type V1AutomationInputDiff,
  type V1AutomationInputSpec,
  type V1AutomationRevisionSummary,
} from '@/generated/hooks/grimoire/models';
import { getRevisionId } from '@/shared/utils/api-utils';
import { FilterBuilder, formatDisplayName } from '@/shared/utils';
import {
  EVersionHistoryMenuItemKey,
  type IVersionHistoryItemData,
  type IVersionHistoryListItem,
  type IVersionHistoryGroup,
  type IVersionHistoryMenuItem,
  type IVersionHistoryMenuLabels,
} from './types';
import i18n from '@/shared/i18n';

const PUBLISHED_VERSION_MENU_KEYS = [
  EVersionHistoryMenuItemKey.CREATE_DRAFT,
] as const;
const DEFAULT_VERSION_MENU_KEYS = [EVersionHistoryMenuItemKey.RESTORE] as const;

const MENU_KEY_CONFIG: Record<
  EVersionHistoryMenuItemKey,
  {
    labelKey: keyof IVersionHistoryMenuLabels;
    icon?: IVersionHistoryMenuItem['icon'];
  }
> = {
  [EVersionHistoryMenuItemKey.CREATE_DRAFT]: {
    labelKey: 'createDraft',
  },
  [EVersionHistoryMenuItemKey.RESTORE]: {
    labelKey: 'restore',
  },
};

const STAGE_FILTER_VALUES: Partial<Record<V1AutomationStage, string>> = {
  [V1AutomationStage.AUTOMATION_STAGE_PUBLISHED]: 'PUBLISHED',
  [V1AutomationStage.AUTOMATION_STAGE_DRAFT]: 'DRAFT',
};

/**
 * Determines if a version should show a badge, and if so, which badge to show.
 * - Draft badge: Only shown if the draft is the top/most recent version.
 *   If the top version is published, there are no active drafts to badge.
 * - Published badge: Shown on the latest published version.
 *
 * @param version - The version to check
 * @param versions - All versions (pre-sorted newest to oldest)
 * @returns True if the version should show a badge
 */
export function shouldShowBadge(
  version: IVersionHistoryListItem,
  versions: IVersionHistoryListItem[],
): boolean {
  if (version.stage === V1AutomationStage.AUTOMATION_STAGE_DRAFT) {
    return versions.length > 0 && versions[0].version === version.version;
  }

  const latestPublishedId = versions.find(
    v => v.stage === V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
  )?.version;

  if (version.stage === V1AutomationStage.AUTOMATION_STAGE_PUBLISHED) {
    return version.version === latestPublishedId;
  }
  return false;
}

/**
 * Groups versions by date, preserving the input order.
 * Assumes versions are already sorted by timestamp descending (newest first).
 *
 * @param items - Array of version entries (pre-sorted newest to oldest)
 * @returns Array of version groups in descending date order
 */
export function groupItemsByDate(
  items: IVersionHistoryListItem[],
): IVersionHistoryGroup[] {
  const groupMap = new Map<string, IVersionHistoryGroup>();

  items.forEach(item => {
    const dateKey = dateToISOString(item.timestamp);

    if (!groupMap.has(dateKey)) {
      // Format with relative labels (Today, Yesterday) or formatted date
      const label = formatRelativeDate(item.timestamp);

      // groupMap: { '2025-12-09': { dateLabel: 'Today', key: '2025-12-09', versions: [] } }
      groupMap.set(dateKey, {
        label,
        dateKey,
        items: [],
      });
    }

    groupMap.get(dateKey)!.items.push(item);
  });

  // Example output, follows IVersionHistoryGroup interface:
  // [
  //   { dateLabel: 'Today', key: '2025-12-10', versions: [v2.2, v2.1, v2.0, ...] },
  //   { dateLabel: 'Yesterday', key: '2025-12-09', versions: [v1.3, v1.2, ...] },
  //   { dateLabel: 'Nov 30, 2025', key: '2025-11-30', versions: [v0.6] },
  // ]
  return Array.from(groupMap.values());
}

/**
 * Checks if a revision version indicates a published version by checking the publish_user_id field.
 *
 * @param publishedUserId - The user ID of the user who published the revision
 * @returns The stage of the revision
 */
export function getStage(publishedUserId?: string): V1AutomationStage {
  return publishedUserId
    ? V1AutomationStage.AUTOMATION_STAGE_PUBLISHED
    : V1AutomationStage.AUTOMATION_STAGE_DRAFT;
}

/**
 * Checks if a revision is the first (most recent) in the list and is a draft.
 * First draft versions should not show menu items.
 *
 * @param revision - The revision to check
 * @param revisions - All revisions (first is most recent)
 * @param stage - The determined stage of the revision
 * @returns True if this is the first version and it's a draft
 */
function isFirstVersionAndDraft(
  revision: V1AutomationRevisionSummary,
  revisions: V1AutomationRevisionSummary[],
  stage: V1AutomationStage,
): boolean {
  return (
    stage === V1AutomationStage.AUTOMATION_STAGE_DRAFT &&
    revisions.length > 0 &&
    revisions[0]?.name === revision.name
  );
}

/**
 * Builds the collapsible subtitle describing how an automation's inputs changed
 * between this revision and the immediately preceding one.
 *
 * - `title` is a summary count (e.g. "2 inputs modified").
 * - `details` lists each individual change on its own line
 *   (e.g. "Subject input added\nDescription input removed").
 *
 * Returns undefined when there is no input diff or it contains no changes
 * (e.g. the earliest revision, which has no predecessor).
 *
 * @param inputDiff - The input-spec diff from the automation revision
 * @returns A subtitle object for VersionHistoryDescription, or undefined
 */
export function formatInputDiffSubtitle(
  inputDiff: V1AutomationInputDiff | undefined,
): { title: string; details: string } | undefined {
  if (!inputDiff) return undefined;

  const added = inputDiff.added ?? [];
  const removed = inputDiff.removed ?? [];
  const modified = inputDiff.modified ?? [];

  const total = added.length + removed.length + modified.length;
  if (total === 0) return undefined;

  const line = (
    spec: V1AutomationInputSpec,
    action: 'added' | 'removed' | 'modified',
  ): string =>
    i18n.t(`automation.versionHistory.inputDiff.${action}`, {
      name: formatDisplayName(spec.name),
      ns: 'automation',
    });

  const details = [
    ...added.map(spec => line(spec, 'added')),
    ...removed.map(spec => line(spec, 'removed')),
    ...modified.map(spec => line(spec, 'modified')),
  ].join('\n');

  return {
    title: i18n.t('automation.versionHistory.inputDiff.summary', {
      count: total,
      ns: 'automation',
    }),
    details,
  };
}

/**
 * Transforms API revision data to version history list items.
 *
 * Reads revision metadata only — name, timestamps, author ids, stage and the
 * input diff. The content fields (`code`, `english_code`, `description`) are
 * blanked out server-side in a summary and are deliberately not read here:
 * content for the selected revision is fetched separately via
 * `useRevisionContents`.
 *
 * @param revisions - Page of automation revision summaries from the API
 * @param menuLabels - Translated labels for menu items
 * @param onMenuItemClick - Callback when a menu item is clicked
 * @returns Array of version history list items
 */
export function transformRevisionsToListItems(
  revisions: V1AutomationRevisionSummary[],
  menuLabels: IVersionHistoryMenuLabels,
  onMenuItemClick: (key: string, versionData: IVersionHistoryItemData) => void,
): IVersionHistoryListItem[] {
  return revisions.map(revision => {
    const revisionId = getRevisionId(revision.name);
    const createTime = revision.create_time
      ? parseTimestamp(revision.create_time)
      : getCurrentDate();

    const stage = getStage(revision.snapshot?.publish_user_id);

    // Prefer the input diff (added/removed/modified inputs vs the previous
    // revision) when present; otherwise fall back to the restore message.
    const inputDiffSubtitle = formatInputDiffSubtitle(revision.input_diff);
    const restoreVersion = revision.snapshot?.restore_version;
    const subtitle =
      inputDiffSubtitle ??
      (restoreVersion
        ? i18n.t('automation.versionHistory.item.restoredFrom', {
            version: restoreVersion,
            ns: 'automation',
          })
        : undefined);

    // First draft version should not have menu items
    const shouldHideMenu = isFirstVersionAndDraft(revision, revisions, stage);
    const isPublishedAndTop =
      stage === V1AutomationStage.AUTOMATION_STAGE_PUBLISHED &&
      revisions[0]?.name === revision.name;

    // Determine the author to display based on stage:
    // - For drafts: use update_user_id (who created/updated this draft) if available, otherwise author_id
    // - For published: use publish_user_id if available, otherwise author_id
    const author =
      stage === V1AutomationStage.AUTOMATION_STAGE_PUBLISHED
        ? revision.snapshot?.publish_user_id ||
          revision.snapshot?.author_id ||
          ''
        : revision.snapshot?.update_user_id ||
          revision.snapshot?.author_id ||
          '';

    const versionData: IVersionHistoryItemData = {
      revisionId: revisionId,
      version: `v${revisionId}`,
      title: formatTimestamp(
        revision.create_time || '',
        DATE_FORMAT_TOKENS.SHORT_DATE_TIME,
      ),
      author,
      stage: stage,
      subtitle,
    };

    return {
      ...versionData,
      timestamp: createTime,
      menuItems: shouldHideMenu
        ? undefined
        : buildMenuItems(isPublishedAndTop, menuLabels, (key: string) =>
            onMenuItemClick(key, versionData),
          ),
    };
  });
}

/**
 * Builds menu items for a version history item based on its state.
 *
 * @param isPublishedAndTop - Whether the item is the top published version
 * @param labels - Translated labels for menu items
 * @param onMenuItemClick - Callback when a menu item is clicked
 * @returns Array of menu items for the dropdown
 */
export function buildMenuItems(
  isPublishedAndTop: boolean,
  labels: IVersionHistoryMenuLabels,
  onMenuItemClick: (key: string) => void,
): IVersionHistoryMenuItem[] {
  const menuKeys = isPublishedAndTop
    ? PUBLISHED_VERSION_MENU_KEYS
    : DEFAULT_VERSION_MENU_KEYS;

  return menuKeys.map(key => {
    const config = MENU_KEY_CONFIG[key];
    return {
      key,
      title: labels[config.labelKey],
      icon: config.icon,
      onClick: () => onMenuItemClick(key),
    };
  });
}

/**
 * Formats the version badge text for a selected version.
 *
 * Combines version number and date title with a bullet separator.
 * Returns empty string if no version is provided.
 *
 * @param selectedVersion - The selected version data
 * @returns Formatted badge text (e.g., "v2.0 • Dec 2, 3:08 PM")
 *
 * @example
 * getVersionBadgeText({ version: 'v2.0', title: 'Dec 2, 3:08 PM', author: 'John' });
 * // Returns: "v2.0 • Dec 2, 3:08 PM"
 */
export function getVersionBadgeText(
  selectedVersion: IVersionHistoryItemData | null | undefined,
): string {
  if (!selectedVersion) {
    return '';
  }

  const { version: versionText, title: dateText } = selectedVersion;

  if (versionText && dateText) {
    return `${versionText} • ${dateText}`;
  }
  return versionText || dateText || '';
}

/**
 * Whether version-history track changes should render for this stage filter.
 *
 * Document view (published automation) opens history filtered to Published.
 * That list is already consecutive published revisions, so the same
 * previous-to-selected diff used for "All versions" is valid. Draft-only stays
 * off until filtered diffs are supported there.
 */
export function versionHistoryShowsTrackChanges(
  stage: V1AutomationStage,
): boolean {
  return (
    stage === V1AutomationStage.AUTOMATION_STAGE_UNSPECIFIED ||
    stage === V1AutomationStage.AUTOMATION_STAGE_PUBLISHED
  );
}

/**
 * Converts V1AutomationStage to CEL filter string for the API.
 * Returns undefined for UNSPECIFIED to fetch all revisions.
 *
 * @param stage - The automation stage to filter by
 * @returns CEL filter string or undefined
 *
 * @example
 * getStageFilterExpression(V1AutomationStage.AUTOMATION_STAGE_PUBLISHED)
 * // Returns: 'stage = "PUBLISHED"'
 */
export function getStageFilterExpression(
  stage: V1AutomationStage,
): string | undefined {
  const filterValue = STAGE_FILTER_VALUES[stage];
  if (!filterValue) return undefined;
  return FilterBuilder.create().equals('stage', filterValue).build();
}
